import { memo } from "react";
import { Highlight } from "prism-react-renderer";
import { useSelector } from "react-redux";
import { THEME_TYPES, THEME_COLORS } from "constants/theme";

const MAX_EDITOR_LINE = 30;
const MIN_EDITOR_LINE = 8;
const CODE_HEIGHT = 20;

const customDarkTheme = {
  plain: {
    color: THEME_COLORS.DARK.PRIMARY_FONT,
    backgroundColor: THEME_COLORS.DARK.PRIMARY_BG
  },
  styles: [
    {
      types: ["property"],
      style: {
        color: "#df6060ff"
      }
    },
    {
      types: ["string"],
      style: {
        color: "#2d87e7ff"
      }
    },
    {
      types: ["number", "boolean"],
      style: {
        color: "#03935eff"
      }
    },
    {
      types: ["punctuation", "operator"],
      style: {
        color: "#cfc7c7ff"
      }
    },
    {
      types: ["comment"],
      style: {
        color: "#75bb54ff",
        fontStyle: "italic"
      }
    }
  ]
};

const customLightTheme = {
  plain: {
    color: THEME_COLORS.LIGHT.PRIMARY_FONT,
    backgroundColor: THEME_COLORS.LIGHT.PRIMARY_BG
  },
  styles: [
    {
      types: ["property"],
      style: {
        color: "#A31515"
      }
    },
    {
      types: ["string"],
      style: {
        color: "#0451A5"
      }
    },
    {
      types: ["number", "boolean"],
      style: {
        color: "#098658"
      }
    },
    {
      types: ["punctuation", "operator"],
      style: {
        color: "#555"
      }
    },
    {
      types: ["comment"],
      style: {
        color: "#6A9955",
        fontStyle: "italic"
      }
    }
  ]
};

export const TimelineEditor = memo(({ defaultValue, language = "json", isWrapped }) => {
  const isDark = useSelector((store) => store?.portal?.settings.themeMode) === THEME_TYPES.DARK;
  const lines = (String(defaultValue).match(/\n/g) || "").length + 1;

  let height;
  if (lines <= MIN_EDITOR_LINE) {
    height = MIN_EDITOR_LINE * CODE_HEIGHT;
  } else if (lines <= MAX_EDITOR_LINE) {
    height = lines * CODE_HEIGHT;
  } else {
    height = MAX_EDITOR_LINE * CODE_HEIGHT;
  }

  const theme = isDark ? customDarkTheme : customLightTheme;

  return (
    <Highlight theme={theme} code={defaultValue} language={language}>
      {({ style, tokens, getLineProps, getTokenProps }) => (
        <pre
          style={{
            ...style,
            margin: 0,
            padding: "12px",
            fontSize: "12px",
            lineHeight: "20px",
            height: `${height}px`,
            overflow: "auto",
            borderRadius: "4px",
            whiteSpace: isWrapped ? "pre-wrap" : "pre",
            wordWrap: isWrapped ? "break-word" : "normal",
            overflowWrap: isWrapped ? "break-word" : "normal"
          }}
        >
          {tokens.map((line, i) => (
            <div key={i} {...getLineProps({ line })}>
              {line.map((token, key) => (
                <span key={key} {...getTokenProps({ token })} />
              ))}
            </div>
          ))}
        </pre>
      )}
    </Highlight>
  );
});
